import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import HourglassFill from '@/assets/icons/hourglass_fill.svg?component';
import InformationLine from '@/assets/icons/information_line.svg?component';
import { Alert } from '@/components/atoms/Alert';
import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import ContentBlock from '@/components/molecules/ContentBlock';
import FishHealthFilter from '@/components/molecules/FishHealthFilter';
import { LoadingBarWithBanner } from '@/components/molecules/LoadingBarWithBanner/LoadingBarWithBanner';
import type { Image } from '@/components/organisms/ImageViewer';
import { ImageViewer } from '@/components/organisms/ImageViewer';
import { usePermissions } from '@/contexts/auth';
import { useCageId, useCageTimezone } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import { usePenCohortFishGroup } from '@/contexts/pen-cohort-context';
import { isValidCageId } from '@/domain/cages';
import type { HealthParameterKey } from '@/domain/health';
import { useGetHealthOverview } from '@/domain/health';
import {
    fullSizeLaksvelFinImageMapper,
    fullSizeLaksvelImageMapper,
    HEALTH_IMAGES_DAYPART_TAG,
    LAKSVEL_FILTER_TAGS,
    useGetHealthImages,
} from '@/domain/images';
import { useImageState } from '@/hooks/image-state';

import { findHealthOverviewParameter } from '../../_utils/healthDetailOverviewHelpers';

import styles from './HealthDetailDetections.module.scss';

const DETECTIONS_VIEWER_HEIGHT = 480;

/**
 * Fin damage is the only Laksvel parameter scored per body part, so it is the only one whose
 * detections can be drawn as boxes: the other eleven score the whole fish and have nothing
 * to outline. Keyed on the image indicator rather than the page id because the indicator is
 * what selects the boxes out of the welfare row.
 */
const FIN_DAMAGE_INDICATOR = 'fin_damage_score';

export interface HealthDetailDetectionsProps {
    healthParameterKey: HealthParameterKey;
    laksvelImageIndicator: string;
}

export default function HealthDetailDetections(props: HealthDetailDetectionsProps) {
    const { healthParameterKey, laksvelImageIndicator } = props;
    const { t } = useTranslation('healthModule');
    const cageId = useCageId();
    const timezone = useCageTimezone() ?? 'UTC';
    const { isOptoScaleAdmin } = usePermissions();
    const { selectedFishGroupId } = useCageFilter();
    const imagesFishGroup = usePenCohortFishGroup();

    // The flag labels the viewer's day images, so it is read from the day estimate whatever
    // daypart the cage filter shows, for the selected fish group like the rest of the page. On
    // the default `day` filter these are the information panel's params, so the response comes
    // from its cache.
    const overviewParams = useMemo(
        () => ({ daypartTag: HEALTH_IMAGES_DAYPART_TAG, fishGroupId: selectedFishGroupId }),
        [selectedFishGroupId]
    );

    const {
        imagesByIndicator,
        isLoading: imagesLoading,
        isFetching: isFetchingImages,
    } = useGetHealthImages(cageId, { fishGroup: imagesFishGroup });

    const { data: overview, isLoading: overviewLoading } = useGetHealthOverview(
        cageId,
        overviewParams,
        { enabled: isValidCageId(cageId), staleTime: 5 * 60 * 1000 }
    );

    const loading = imagesLoading || overviewLoading;
    const showLaksvelImageViewer =
        !loading && findHealthOverviewParameter(overview, healthParameterKey)?.showImageViewer;

    const [selectedImageFilterTag, setSelectedImageFilterTag] = useState<string>(
        () => LAKSVEL_FILTER_TAGS[0]?.tag ?? 'all'
    );

    const laksvelFilter = useCallback(
        (image: Image) => {
            if (selectedImageFilterTag === 'all') {
                return true;
            }
            return image.score === selectedImageFilterTag;
        },
        [selectedImageFilterTag]
    );

    const showFinBoxes = laksvelImageIndicator === FIN_DAMAGE_INDICATOR;

    const imageMapper = useMemo(
        () =>
            showFinBoxes
                ? fullSizeLaksvelFinImageMapper(cageId, laksvelImageIndicator)
                : fullSizeLaksvelImageMapper(cageId, laksvelImageIndicator),
        [cageId, laksvelImageIndicator, showFinBoxes]
    );

    const { dayswithimages, images, initialImageIndex, onAfterTimelineChangeHandler } =
        useImageState({
            data: imagesByIndicator[laksvelImageIndicator],
            mapper: imageMapper,
            loading,
            timezone,
            queryParam: 'imageId',
        });

    const filteredImages = useMemo(() => images.filter(laksvelFilter), [images, laksvelFilter]);

    const imageViewerKey = useMemo(
        () =>
            `laksvelDetections-${laksvelImageIndicator}-${selectedImageFilterTag}-${initialImageIndex}`,
        [initialImageIndex, laksvelImageIndicator, selectedImageFilterTag]
    );

    /* 'off' rather than 'hidden' would show the toggle but start with the boxes down; the
       fin scores are the point of this page, so they start up. Every other parameter keeps
       'hidden', which suppresses the toggle as well as the boxes. */
    const viewerOptions = useMemo(
        () => ({ boxes: showFinBoxes ? ('on' as const) : ('hidden' as const), showHelp: false }),
        [showFinBoxes]
    );

    return (
        <ContentBlock title={t('detail.common.detectionsTitle')} size="compact" spacing="none">
            <div className={styles.detections} data-testid="health-detail-detections">
                {loading ? (
                    <SkeletonBox width="100%" height={DETECTIONS_VIEWER_HEIGHT} borderRadius={8} />
                ) : (
                    <>
                        {isFetchingImages ? (
                            <LoadingBarWithBanner
                                showLoadingBar={true}
                                bottomLoadingEdgesRound="all"
                                surface="plain">
                                <div className={styles.loadingTextContainer}>
                                    <SvgIcon
                                        icon={HourglassFill}
                                        color="var(--color-success-fill)"
                                    />
                                    <h4>{t('detail.common.loadingImages')} ...</h4>
                                </div>
                                {t('detail.common.justFewMoreSeconds')}
                            </LoadingBarWithBanner>
                        ) : null}
                        {showLaksvelImageViewer ? (
                            <div className={styles.imageWrapper}>
                                <ImageViewer
                                    key={imageViewerKey}
                                    variant="laksvelDetections"
                                    initialImageIndex={initialImageIndex}
                                    images={filteredImages}
                                    daysWithImages={dayswithimages}
                                    options={viewerOptions}
                                    isColor={true}
                                    isOptoScaleAdmin={isOptoScaleAdmin}
                                    onDayChange={onAfterTimelineChangeHandler}
                                />
                                <div className={styles.filterOptions}>
                                    <FishHealthFilter
                                        filterStateList={LAKSVEL_FILTER_TAGS}
                                        onFilterGroupChange={setSelectedImageFilterTag}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className={styles.imageWrapper}>
                                <Alert
                                    tone="warning"
                                    icon={<SvgIcon icon={InformationLine} color="currentColor" />}>
                                    {t('detail.common.modelFineTuneLead')}
                                    <br />
                                    {t('detail.common.modelFineTuneBody')}
                                </Alert>
                                <div className={styles.imageWrapperNoDetections}>
                                    <ImageViewer
                                        key={imageViewerKey}
                                        variant="healthDetections"
                                        initialImageIndex={initialImageIndex}
                                        images={filteredImages}
                                        daysWithImages={dayswithimages}
                                        options={viewerOptions}
                                        isColor={true}
                                        isOptoScaleAdmin={isOptoScaleAdmin}
                                        onDayChange={onAfterTimelineChangeHandler}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </ContentBlock>
    );
}
